import React from 'react';
import { cn } from '@/hen-ui/utils';
import { Image } from '@/hen-ui/atomics/Image';
import { imageCardVariants } from './image-card.variants';

const imageBackgroundColorClasses = {
  'neutral-50': 'bg-neutral-50',
  'neutral-100': 'bg-neutral-100',
  white: 'bg-white',
  transparent: 'bg-transparent',
} as const;

const imageAspectClasses = {
  default: '',
  video: 'aspect-video',
  square: 'aspect-square',
} as const;

export type ImageCardDefaultProps = {
  /** Image source URL */
  imageUrl: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Optional styling for the image */
  imgClass?: string;
  /** Background color behind the image (uses wrapper div; prefer over imgClass) */
  imageBackgroundColor?: keyof typeof imageBackgroundColorClasses;
  /** Aspect ratio of the image area (prefer over imgClass) */
  imageAspect?: keyof typeof imageAspectClasses;
  /** Defines the content area under the image and supports any valid React node (text, buttons, etc.) */
  children: React.ReactNode;
  /** Optional configuration props (e.g., className, aria-*) for styling and accessibility */
  className?: string;
};

export const ImageCardDefault: React.FC<ImageCardDefaultProps> = ({
  imageUrl,
  imageAlt,
  imgClass,
  imageBackgroundColor,
  imageAspect = 'default',
  children,
  className,
}) => {
  const imageEl = (
    <Image
      src={imageUrl}
      alt={imageAlt}
      loading='lazy'
      decoding='async'
      rounded
      objectFit='cover'
      className={cn(
        'w-full h-auto flex-shrink-0',
        imageAspectClasses[imageAspect],
        imgClass,
      )}
    />
  );

  const wrappedImage =
    imageBackgroundColor &&
    imageBackgroundColorClasses[imageBackgroundColor] ? (
      <div
        className={cn(
          'rounded-xl overflow-hidden',
          imageBackgroundColorClasses[imageBackgroundColor],
        )}
      >
        {imageEl}
      </div>
    ) : (
      imageEl
    );

  return (
    <div
      className={cn(imageCardVariants({ variant: 'default' }), className)}
      role='article'
      aria-label={imageAlt}
    >
      {wrappedImage}
      {children && (
        <div className='flex flex-col flex-shrink-0 gap-xs'>{children}</div>
      )}
    </div>
  );
};
